import { Component, EventEmitter, Input, Output } from "@angular/core";
import { FormArray, FormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material";
import * as XLSX from "xlsx";
import { DownloadFileFormatPromoRulesDialog } from "../create-promotion-rules.component";

@Component({
  selector: "app-promo-rule-item",
  templateUrl: "./promo-rule-item.component.html",
  styleUrls: ["./promo-rule-item.component.scss"],
})
export class PromoRuleItemComponent {
  @Input() ruleItem: FormGroup;
  @Input() index: number;
  @Input() reqIndex: number;
  @Input() parentArray: FormArray;
  @Input() criteriaPossibleValuesList: any[] = [];
  @Input() rulesConditionList: any[] = [];
  @Input() dropdownSettings: any = {};
  @Input() childCriteriasCondition: string;
  @Input() questionList: any;

  @Output() siblingConditionChanged = new EventEmitter<string>();
  @Output() addRuleInsideGroupEvent = new EventEmitter<FormGroup>();
  @Output() addGroupInsideGroupEvent = new EventEmitter<FormGroup>();

  questionRegex = /##[0-9]##/;
  seperator = "###";
  fileUploadContext: { ruleItem: FormGroup | null } = { ruleItem: null };

  constructor(private dialog: MatDialog) {}

  getChildCriterias(ruleItem: FormGroup): FormArray {
    return ruleItem.get("criterias") as FormArray;
  }

  removeRule(parentArray: FormArray, index: number) {
    parentArray.removeAt(index);
  }

  setSiblingCondition(event: any, parentArray: FormArray) {
    const value = event.target.value;
    parentArray.controls.forEach((control: FormGroup) => {
      control.get("ruleCondition").setValue(value);
    });

    const parentGroup = parentArray.parent as FormGroup;
    if (
      parentGroup &&
      parentGroup.get("type") &&
      parentGroup.get("type").value === "GROUP"
    ) {
      parentGroup.get("childCondition").patchValue(value, { emitEvent: false });
    }
    this.siblingConditionChanged.emit(value);
  }

  getSelectionList(ruleItem: FormGroup, type: string) {
    if (!this.criteriaPossibleValuesList.length) {
      return null;
    }

    let ruleSelectionList: any = {
      productList: null,
      filterList: null,
      inputType: "textinput",
      ruleValueList: null,
      valDropdownList: null,
    };

    const selectedRule = ruleItem.get("ruleSelection").value;
    const selectedProduct = ruleItem.get("productSelection").value;

    let selectedRuleIndex = 0;
    let selectedProductIndex = 0;

    if (selectedRule) {
      selectedRuleIndex = this.criteriaPossibleValuesList.findIndex(
        (x) => x.value == selectedRule
      );

      if (selectedRuleIndex > -1) {
        ruleSelectionList.productList =
          this.criteriaPossibleValuesList[selectedRuleIndex].possibleValues;
      }
    }

    if (selectedProduct && selectedRuleIndex > -1) {
      selectedProductIndex = this.criteriaPossibleValuesList[selectedRuleIndex]
        .possibleValues
        .findIndex((x) => x.value == selectedProduct);

      if (selectedProductIndex > -1) {
        ruleSelectionList.filterList =
          this.criteriaPossibleValuesList[selectedRuleIndex]
            .possibleValues[selectedProductIndex].possibleValues;
      }
    }

    if (selectedProduct && selectedRuleIndex > -1 && selectedProductIndex > -1) {
      const selectedProductObj =
        this.criteriaPossibleValuesList[selectedRuleIndex]
          .possibleValues[selectedProductIndex];

      const formFieldType = selectedProductObj.formFieldFormat;

      if (formFieldType === "CURRENCY") {
        ruleSelectionList.inputType = "textinput";
      } else if (formFieldType === "DROPDOWN") {
        ruleSelectionList.inputType = "dropdown";
        ruleSelectionList.valDropdownList =
          selectedProductObj.possibleValues[0].possibleValues;
      } else if (formFieldType === "EXCEL") {
        ruleSelectionList.inputType = "excelupload";
        ruleSelectionList.ruleValueList = ruleItem.get("ruleValueSelection").value;
      } else if (formFieldType === "EXCEL_MERGE") {
        ruleSelectionList.inputType = "excelmerge";
        ruleSelectionList.ruleValueList = ruleItem.get("ruleValueSelection").value;
      } else if (formFieldType === "DROPDOWN_MULTIPLE") {
        ruleSelectionList.inputType = "dropdown_multiple";
        ruleSelectionList.valDropdownList =
          selectedProductObj.possibleValues[0].possibleValues;
      } else {
        ruleSelectionList.inputType = "";
      }
    }

    return ruleSelectionList[type];
  }

  setIntialData(ruleItem: FormGroup) {
    ruleItem.get("ruleValueSelection").setValue(null);
    ruleItem.get("uploadedFileName").setValue(null);
  }

  upload(ruleItem: FormGroup, fileInput: HTMLInputElement) {
    this.fileUploadContext.ruleItem = ruleItem;
    fileInput.click();
  }

  uploadExcelmerge(ruleItem: FormGroup, fileInput: HTMLInputElement) {
    this.fileUploadContext.ruleItem = ruleItem;
    fileInput.click();
  }

  getFile(event: any, ruleItem: FormGroup, fileInput: HTMLInputElement) {
    const target = event.target as HTMLInputElement;
    if (!target.files || target.files.length !== 1) {
      throw new Error("Cannot use multiple files");
    }
    const file = target.files[0];
    const reader: FileReader = new FileReader();

    reader.readAsBinaryString(file);
    reader.onload = (e: any) => {
      const binarystr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: "binary" });

      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      const data = XLSX.utils.sheet_to_json(ws);

      this.setDataInDropDown(data, ruleItem);
      ruleItem.patchValue({ uploadedFileName: file.name });
      fileInput.value = "";
    };
  }

  getFileExcelMerge(
    event: Event,
    ruleItem: FormGroup,
    fileInput: HTMLInputElement
  ) {
    const input = event.target as HTMLInputElement;
    const file = input.files && input.files[0];
    if (!file) return;

    if (input.files.length > 1) {
      throw new Error("Cannot use multiple files");
    }

    ruleItem.patchValue({ uploadedFileName: file.name });

    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = (e: any) => {
      const binarystr: string = e.target.result;
      const wb: XLSX.WorkBook = XLSX.read(binarystr, { type: "binary" });

      const wsname: string = wb.SheetNames[0];
      const ws: XLSX.WorkSheet = wb.Sheets[wsname];

      const data = XLSX.utils.sheet_to_json(ws);

      fileInput.value = "";
      this.setMergeDataInDropDown(data, ruleItem);
    };
  }

  setDataInDropDown(data: any[], ruleItem: FormGroup) {
    const columnName = Object.keys(data[0])[0];
    const skuList = data.map((val) => val[columnName].toString());
    ruleItem.patchValue({ ruleValueSelection: skuList });
  }

  setMergeDataInDropDown(data: any[], ruleItem: FormGroup) {
    const skuList: string[] = [];
    const showDataList: any[] = [];

    data.forEach((val) => {
      const joinedValue = Object.values(val).join(this.seperator).toString();
      skuList.push(joinedValue);
      showDataList.push({
        id: joinedValue,
        text:
          joinedValue.split(this.seperator)[0] +
          " " +
          `(${joinedValue.split(this.seperator).slice(1).join(") (")})`,
      });
    });

    ruleItem.get("ruleValueSelection").setValue(showDataList);
  }

  showDownloadExcelFormatDialog(ruleItem: FormGroup, listType: string) {
    const dropdownData = this.getSelectionList(ruleItem, listType);
    const selectedVal = ruleItem.get("productSelection").value;

    if (!dropdownData || !selectedVal) return;

    const selectedDropdownData = dropdownData.find(
      (x) => x.value === selectedVal
    );
    if (!selectedDropdownData) return;

    const dialogRef = this.dialog.open(DownloadFileFormatPromoRulesDialog, {
      width: "514px",
      data: selectedDropdownData,
    });
    dialogRef.afterClosed().subscribe(() => {});
  }
}
